import React, { FC, useMemo, RefObject, useContext, createContext } from 'react';
import cx from 'classnames';
import nextId from 'react-id-generator';
import { PartsProduct, ProductWithCategory } from '@app/react/const/type';
import {
    ProductThumbnail,
    OnSelectProductHandler,
} from '@root/app/react/uikit/ProductThumbnail';

interface ContextValue {
    testTwoCol: (category: string, subCategory: string) => boolean;
    productThumbnailType: string | null;
}

const LayoutContext = createContext<ContextValue>({
    testTwoCol: () => false,
    productThumbnailType: null,
});

export const useLayoutContext = (): ContextValue => useContext(LayoutContext);

export const LayoutContextProvider = LayoutContext.Provider;

export const SELECT_ALL = 0;

interface DesignContainerProps {
    children: React.ReactNode;
}

export const DesignContainer: FC<DesignContainerProps> = ({ children }) => (
    <div className="design-container">{children}</div>
);

interface CateogryBoxProps {
    categoryId?: number;
    subCategoryId?: number;
    categoryData: Array<{ categoryId: number; categoryName: string, length: number }>;
    subCategoryData?: Array<{ subCategoryId: number; subCategoryName: string, length: number }>;
    categoryChange: React.ChangeEventHandler<HTMLSelectElement>;
    subCategoryChange?: React.ChangeEventHandler<HTMLSelectElement>;
    disableSubCategory?: boolean;
}

export const CateogryBox: FC<CateogryBoxProps> = (props) => {
    const {
        categoryId = SELECT_ALL,
        subCategoryId = SELECT_ALL,
        categoryData,
        subCategoryData = [],
        categoryChange,
        subCategoryChange,
        disableSubCategory = false,
    } = props;

    const allLength = useMemo(() => (
        categoryData.reduce((acc, cat) => cat.length + acc, 0)
    ), [categoryData]);

    return (
        <div className="div-select-category design-container__header">
            <select
                name="category"
                id="1"
                className="select-category"
                value={categoryId}
                onChange={categoryChange}
            >
                <option value={SELECT_ALL}>{`カテゴリー (${allLength})`}</option>
                {categoryData.map((v) => (
                    <option
                        key={v.categoryId}
                        value={v.categoryId}
                        data-value={v}
                    >
                        {`${v.categoryName} (${v.length})`}
                    </option>
                ))}
            </select>
            {disableSubCategory === false && (
                <select
                    name="subCategory"
                    id="2"
                    className="select-category"
                    value={subCategoryId}
                    onChange={subCategoryChange}
                >
                    <option value={SELECT_ALL}>サブカテゴリー</option>
                    {subCategoryData.map((v) => (
                        <option
                            key={v.subCategoryId}
                            value={v.subCategoryId}
                            data-value={v}
                        >
                            {`${v.subCategoryName} (${v.length})`}
                        </option>
                    ))}
                </select>
            )}
        </div>
    );
};

interface SubCategoryListProps {
    categoryName?: string;
    subCategoryName: string;
    products: Array<PartsProduct>;
    selectedProduct: { id: number } | null;
    onSelectProduct: OnSelectProductHandler;
    htmlId: string;
    allDisabled?: boolean;
}

const SubCategoryList: FC<SubCategoryListProps> = (props) => {
    const {
        categoryName,
        subCategoryName = '',
        products = [],
        selectedProduct = null,
        onSelectProduct,
        htmlId,
        allDisabled = false,
    } = props;

    const lc = useLayoutContext();
    const enableTwoCol = lc.testTwoCol(categoryName ?? '', subCategoryName);
    const className = enableTwoCol ? 'type-c' : (lc.productThumbnailType ?? undefined);

    return (
        <section>
            <p className="category-label2">{`${subCategoryName} (${products.length})`}</p>
            <ul className={cx('product-container', { 'two-col': enableTwoCol })}>
                {products.map((product) => {
                    const checked = selectedProduct?.id === product.id;
                    return (
                        <ProductThumbnail
                            className={className}
                            key={product.id}
                            product={product}
                            onClick={onSelectProduct}
                            checked={checked}
                            groupName={htmlId}
                            disabled={allDisabled}
                        />
                    );
                })}
            </ul>
        </section>
    );
};

interface CategoryListProps {
    categoryName: string;
}

const CategoryList: FC<CategoryListProps> = (props) => {
    const { categoryName = '', children } = props;

    return (
        <section>
            <div className="category-label-div">
                <p className="category-label1">{categoryName}</p>
            </div>
            <div className="category-label-div">{children}</div>
        </section>
    );
};

interface CategoryListsProps {
    rootRef?: RefObject;
    selectedProduct: { id: number } | null;
    onSelectProduct: OnSelectProductHandler;
    viewData: ProductWithCategory;
    htmlId: string;
    allDisabled?: boolean;
}

export const CategoryLists: FC<CategoryListsProps> = ({
    rootRef,
    selectedProduct,
    onSelectProduct,
    viewData,
    htmlId,
    allDisabled = false,
}) => (
    <div className="container-overflow" ref={rootRef}>
        {viewData.map((category) => (
            <CategoryList
                key={category.categoryId}
                categoryName={category.categoryName}
            >
                {category.subCategoryData.map((subCategory) => (
                    <SubCategoryList
                        key={subCategory.subCategoryId}
                        categoryName={category.categoryName}
                        subCategoryName={
                        subCategory.subCategoryName || ''
                        }
                        products={subCategory.products}
                        selectedProduct={selectedProduct}
                        onSelectProduct={onSelectProduct}
                        htmlId={htmlId}
                        allDisabled={allDisabled}
                    />
                ))}
            </CategoryList>
        ))}
    </div>
);

export interface OrderProductCategoryListProps extends CateogryBoxProps {
    rootRef?: RefObject;
    selectedProduct: { id: number } | null;
    onSelectProduct: OnSelectProductHandler;
    viewData: ProductWithCategory;
    allDisabled?: boolean;
    disableSubCategory?: boolean;
}

export const OrderProductCategoryList: FC<OrderProductCategoryListProps> = (
    props,
) => {
    const {
        rootRef,
        categoryId,
        subCategoryId,
        categoryData,
        subCategoryData = [],
        categoryChange,
        subCategoryChange,
        selectedProduct,
        onSelectProduct,
        viewData,
        allDisabled = false,
        disableSubCategory = false,
    } = props;

    const htmlId = useMemo(() => nextId('order-product-category-list-'), []);

    return (
        <DesignContainer>
            <CateogryBox
                categoryId={categoryId}
                subCategoryId={subCategoryId}
                categoryData={categoryData}
                subCategoryData={subCategoryData}
                categoryChange={categoryChange}
                subCategoryChange={subCategoryChange}
                disableSubCategory={disableSubCategory}
            />
            <CategoryLists
                rootRef={rootRef}
                selectedProduct={selectedProduct}
                onSelectProduct={onSelectProduct}
                viewData={viewData}
                htmlId={htmlId}
                allDisabled={allDisabled}
            />
        </DesignContainer>
    );
};
